import type { GroupSettings, GroupSettingsPatch } from '#/services/settings/schema.js'
import type { FieldSpec, IntField, SectionKey, SectionSpec } from '#/services/settings/spec.js'
import { WELCOME_NAME_TOKEN } from '#/services/settings/limits.js'
import { buildFieldPatch, readFieldValue, SECTIONS } from '#/services/settings/spec.js'
import { clear, el } from './dom.js'
import { chevronIcon, sectionIcon } from './icons.js'
import { getLang, t } from './labels.js'
import { openHelp } from './sheet.js'
import { openTextEditor } from './text-editor.js'

// The form, split into a PURE core and a thin DOM transport (mirrors the bot's render.ts):
//   • planForm(settings)  → a plain data model of every control (no DOM, no Telegram) — UNIT-TESTED.
//   • renderForm(root, …) → builds DOM from the model and wires change → onChange — live-verify only.
// Both render from the SHARED spec (SECTIONS), so the Mini App and the inline /config panel can never
// diverge on the configurable surface. Enums/ints use native <select> (the web can afford it where the
// inline panel could only cycle/step) — each option carries a spec-domain value, so no nextEnum/
// stepLadder is needed client-side. Free-text fields (welcomeText, blocklist, allowlistDomains) show as a
// tappable row (label + one-line preview) that opens the full-screen editor sheet (text-editor.ts, user
// call session 70 — the old inline textareas were too cramped to write in); list fields hold one entry per
// line there, parsed back to string[] on save (the in-group commands /setwelcome, /addfilter, /allowdomain
// still work too).

// Spec/schema TYPES come from the leaf modules, not the barrel (see patch.ts — keeps server deps out
// of the browser bundle): GroupSettings/GroupSettingsPatch from schema.js, the field-kind union from
// spec.js. All type-only, so esbuild erases them.

export type Control
  = | { kind: 'bool', field: string, label: string, value: boolean, master: boolean, patch: (v: boolean) => GroupSettingsPatch }
    | { kind: 'enum', field: string, label: string, value: string, choices: ReadonlyArray<{ value: string, label: string }>, patch: (v: string) => GroupSettingsPatch }
    | { kind: 'int', field: string, label: string, value: number, choices: ReadonlyArray<{ value: number, label: string }>, patch: (v: number) => GroupSettingsPatch }
    | { kind: 'text', field: string, label: string, value: string, list: boolean, maxLen: number, maxItems: number, entry?: 'domain' | 'userid' | undefined, hint?: string | undefined, suggest?: 'timezone' | undefined, patch: (v: string | string[]) => GroupSettingsPatch }

export interface SectionModel { key: SectionKey, title: string, controls: Control[] }
export interface FormModel { sections: SectionModel[] }

// Mirrors render.ts formatInt: the zero-label when value === 0 (e.g. "off"/"never"), else value+unit.
function formatInt(field: IntField, value: number): string {
  if (value === 0 && field.zeroLabelKey !== undefined)
    return t(field.zeroLabelKey)
  return field.unit ? `${value}${field.unit}` : String(value)
}

/**
 * PURE: the one-line readout a text row shows beside its label — the collapsed face of the editor sheet.
 * Mirrors the bot's textFieldLine (config/render.ts): a LIST shows its entry count (the value is the
 * stored items '\n'-joined, so lines = items); a SCALAR shows its head — whitespace runs (newlines
 * included) folded to single spaces so one line can't smuggle a line break — capped at 40 chars with an
 * ellipsis, or the localized "(default)" marker when empty.
 */
export function textRowPreview(value: string, list: boolean): string {
  if (list)
    return String(value === '' ? 0 : value.split('\n').length)
  const s = value.replace(/\s+/g, ' ').trim()
  if (s.length === 0)
    return t('cfg-default')
  return s.length > 40 ? `${s.slice(0, 40)}…` : s
}

function planControl(settings: GroupSettings, section: SectionSpec, f: FieldSpec): Control {
  const label = t(f.labelKey)
  // `master`: the section's on/off switch — the field the spec labels «Enabled» (captcha, night mode, flood,
  // raid, and AI Shield's root `aiShield`). The «Pannello» look draws it as a key with a light (index.html).
  if (f.kind === 'bool')
    return { kind: 'bool', field: f.field, label, value: readFieldValue(settings, section, f.field) === true, master: f.labelKey === 'cfg-f-enabled', patch: v => buildFieldPatch(section, f.field, v) }
  if (f.kind === 'enum') {
    // Localize each option (cfg-opt-<value>) into the PURE model — mirrors int's {value,label} choices, so
    // the <select> shows a translated label while its option VALUE stays the raw spec enum (the patch value).
    const choices = f.options.map(v => ({ value: v, label: t(`cfg-opt-${v}`) }))
    return { kind: 'enum', field: f.field, label, value: String(readFieldValue(settings, section, f.field)), choices, patch: v => buildFieldPatch(section, f.field, v) }
  }
  if (f.kind === 'int') {
    const value = Number(readFieldValue(settings, section, f.field))
    const choices = f.ladder.map(v => ({ value: v, label: formatInt(f, v) }))
    // The schema bounds are WIDER than the ladder (e.g. flood.messages accepts 2–100; the ladder is a
    // curated subset), so a persisted value can sit off the ladder. Inject it as a selectable option —
    // never let the <select> silently fall back to ladder[0] and then SAVE that over the real value (a
    // green-that-lies). Mirrors the bot's stepLadder, which tolerates an off-ladder current value.
    if (Number.isFinite(value) && !f.ladder.includes(value)) {
      choices.push({ value, label: formatInt(f, value) })
      choices.sort((a, b) => a.value - b.value)
    }
    return { kind: 'int', field: f.field, label, value, choices, patch: v => buildFieldPatch(section, f.field, v) }
  }
  // kind === 'text': editable. A list field (blocklist/allowlistDomains) holds one entry per line; a scalar
  // (welcomeText) the string as-is. list-ness + the bounds come from the SPEC (not a runtime type sniff),
  // so the emitted patch shape is declared, not guessed. The editor sheet validates via parseList before patch().
  const list = f.list === true
  const raw = readFieldValue(settings, section, f.field)
  const value = list ? (raw as string[]).join('\n') : (typeof raw === 'string' ? raw : '')
  // A scalar field may carry a hint (welcomeText → the {name} placeholder). Composed in the PURE model so the
  // i18n string stays placeholder-free (the web codegen rejects a `{` in a web key) — the literal {name} comes
  // from the ONE shared constant, the localized phrase from the label key.
  const hint = f.hintKey !== undefined ? `${WELCOME_NAME_TOKEN} = ${t(f.hintKey)}` : undefined
  // `suggest` (timezone) rides on the text control: renderControl draws the zone DROPDOWN + live clock
  // (free-text <input> fallback) instead of the <textarea>. Carried in the PURE model so the render branch is
  // spec-driven, not a field-name sniff; only meaningful on a scalar (a list keeps the textarea).
  // buildFieldPatch nests it like any text field.
  const suggest = list ? undefined : f.suggest
  return { kind: 'text', field: f.field, label, value, list, maxLen: f.maxLen, maxItems: f.maxItems ?? 0, entry: f.entry, hint, suggest, patch: v => buildFieldPatch(section, f.field, v) }
}

/**
 * PURE: project the settings + the shared spec into a renderable, testable control model. `sections`
 * defaults to the WHOLE shared spec; main.ts passes a subset to render the AI Shield section into its own
 * hero container (which also carries the activity row) and the rest into the ordinary form — same table,
 * split transport, so the two slices can never drift from the spec.
 */
export function planForm(settings: GroupSettings, sections: readonly SectionSpec[] = SECTIONS): FormModel {
  return {
    sections: sections.map(section => ({
      key: section.key,
      title: t(section.titleKey),
      controls: section.fields.map(f => planControl(settings, section, f)),
    })),
  }
}

/**
 * Whether a control is INERT given the current settings — a UI greys it out and disables it. Two rules,
 * both mirroring the bot: (1) a section with an `enabled:false` master greys its OTHER rows (R4); (2) a
 * field that declares `activeWhen` is inert unless its named sibling holds one of the allowed values
 * (e.g. the captcha timeout/failAction do nothing while `type` is 'none'). PURE → unit-tested, no DOM.
 */
export function controlInactive(settings: GroupSettings, sectionKey: SectionKey, field: string): boolean {
  if (field === 'enabled')
    return false // the master toggle stays live so a disabled section can always be turned back on
  const section = SECTIONS.find(s => s.key === sectionKey)
  if (section === undefined)
    return false
  if (section.fields.some(f => f.field === 'enabled') && readFieldValue(settings, section, 'enabled') !== true)
    return true
  const f = section.fields.find(x => x.field === field)
  if (f?.activeWhen !== undefined && !f.activeWhen.in.includes(String(readFieldValue(settings, section, f.activeWhen.field))))
    return true
  return false
}

// ---- DOM transport (live-verify only) ----

// The night-mode timezone picker (v8, user call): a DROPDOWN over the runtime's OWN IANA zone db
// (Intl.supportedValuesOf — browser-native, nothing baked into the bundle), grouped by region via
// <optgroup>, with 'UTC' pinned first (it's the schema default and ICU's canonical list omits it). The
// <select> is built ONCE per session and MOVED into each render's row: the form is fully rebuilt on every
// save, and regenerating ~420 <option> nodes on every unrelated edit is needless jank on a low-end mobile
// webview (the same cold-eye finding that made v7's datalist a body singleton). Its ONE 'change' listener
// is wired at build time and dispatches through `tzOnPick`, which each render re-points at the CURRENT
// control's patch. An engine without supportedValuesOf gets the free-text <input> fallback in
// renderControl — degrade, don't lock the field. Touches `document` only when CALLED (render time), so
// the pure planForm core stays importable in the Node test environment.
let tzSelect: HTMLSelectElement | null = null
let tzOnPick: ((zone: string) => void) | null = null

function ensureTimezoneSelect(): HTMLSelectElement | null {
  if (tzSelect)
    return tzSelect
  const hasIntlZones = typeof (Intl as { supportedValuesOf?: unknown }).supportedValuesOf === 'function'
  const zones = hasIntlZones ? Intl.supportedValuesOf('timeZone') : []
  if (zones.length === 0)
    return null
  const select = el('select', {}) as HTMLSelectElement
  select.append(el('option', { value: 'UTC' }, 'UTC'))
  const groups = new Map<string, HTMLElement>()
  for (const zone of zones) {
    if (zone === 'UTC')
      continue // already pinned first — engines DISAGREE on listing it (Firefox does, V8 doesn't); skip the dupe
    const slash = zone.indexOf('/')
    if (slash === -1) { // no-region zones vary by engine (V8 lists none, others may) — ungrouped, after UTC
      select.append(el('option', { value: zone }, zone))
      continue
    }
    const region = zone.slice(0, slash)
    let group = groups.get(region)
    if (group === undefined) {
      group = el('optgroup', { label: region })
      groups.set(region, group)
      select.append(group)
    }
    group.append(el('option', { value: zone }, zone))
  }
  select.addEventListener('change', () => tzOnPick?.(select.value))
  tzSelect = select
  return select
}

// Point the singleton at the CURRENT stored value: refresh the (localizable) aria-label, drop a previous
// render's injected option, and select the value — injecting it as a temporary option when it isn't in
// THIS browser's zone list (client↔server ICU skew, or an exotic hand-set zone). The select must never
// silently show — and on the next change save over — a different zone than the one actually stored (the
// same green-that-lies rule as the off-ladder int injection above).
function syncTimezoneSelect(select: HTMLSelectElement, value: string, label: string): void {
  select.setAttribute('aria-label', label)
  select.querySelector('option[data-injected]')?.remove()
  if (![...select.options].some(o => o.value === value))
    select.prepend(el('option', { 'value': value, 'data-injected': 'true' }, value))
  select.value = value
}

/**
 * PURE (unit-tested): the zone-clock text — the current time (24h, matching the night-mode 0–23 hour
 * fields) plus the UTC offset in `zone`, localized. Fallback chain: an ICU too old for `shortOffset`
 * still gets the plain time; a zone THIS engine rejects outright returns null — no clock beats a wrong
 * clock (the caller renders an honest "—").
 */
export function formatZoneClock(zone: string, locale: string, now: Date): string | null {
  const base: Intl.DateTimeFormatOptions = { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
  try {
    return new Intl.DateTimeFormat(locale, { ...base, timeZoneName: 'shortOffset' }).format(now)
  }
  catch {
    try {
      return new Intl.DateTimeFormat(locale, base).format(now)
    }
    catch {
      return null
    }
  }
}

// The live clock CHIP beside the picker — the "is the bot's midnight my midnight?" check. Re-created on
// every render (the zone may have just changed); ONE module-level interval keeps the minutes honest while
// the app sits open, always writing into the CURRENT chip node (an older node is detached with its row by
// the repaint, so a stale write is impossible by construction). The interval is deliberately never
// cleared: it's a single 30s tick for the app's lifetime.
let tzClock: HTMLElement | null = null
let tzClockZone = ''
let tzClockTimer = 0

function paintTzClock(): void {
  if (tzClock)
    tzClock.textContent = formatZoneClock(tzClockZone, getLang(), new Date()) ?? '—'
}

function mountTzClock(zone: string): HTMLElement {
  tzClockZone = zone
  tzClock = el('span', { class: 'tz-clock' })
  paintTzClock()
  if (tzClockTimer === 0)
    tzClockTimer = window.setInterval(paintTzClock, 30_000)
  return tzClock
}

function renderControl(control: Control, sectionKey: SectionKey, onChange: (patch: GroupSettingsPatch) => void): HTMLElement {
  if (control.kind === 'text') {
    // The timezone field (v8, user call): a region-grouped DROPDOWN over the runtime's IANA zone db, with
    // the live zone CLOCK beside it (mountTzClock — current time + UTC offset in the SAVED zone). The
    // <select> is the session singleton (ensureTimezoneSelect); a pick commits immediately like every
    // other select. ⚠️ Known, DELIBERATE narrowing vs the v7 free-text input: the dropdown offers only
    // the client ICU's CANONICAL zones, so a server-valid legacy ALIAS ('US/Eastern', 'Asia/Calcutta')
    // can no longer be TYPED here — every real place stays reachable via its canonical name, an alias
    // already STORED still shows (syncTimezoneSelect injects it), and /setnighttz still accepts anything
    // the schema does. Client↔server ICU skew on a canonical zone stays the same rare, LOUD 400 as v7
    // (reverts with the error banner, never silent). An engine with no zone db at all falls back to the
    // free-text <input> — the backend schema stays the real gate either way.
    if (control.suggest === 'timezone') {
      let field: HTMLElement
      const select = ensureTimezoneSelect()
      if (select) {
        tzOnPick = (zone) => {
          if (zone !== control.value)
            onChange(control.patch(zone))
        }
        syncTimezoneSelect(select, control.value, control.label)
        field = select
      }
      else {
        const input = el('input', {
          'type': 'text', // a zone is text (NOT inputmode=numeric)
          'class': 'text-input',
          'aria-label': control.label,
          'maxlength': String(control.maxLen),
          'value': control.value,
          'autocapitalize': 'off', // "europe/rome", not "Europe/Rome" auto-cased on mobile
          'spellcheck': 'false',
        }) as HTMLInputElement
        input.addEventListener('change', () => {
          const v = input.value.trim()
          if (v !== control.value)
            onChange(control.patch(v))
        })
        field = input
      }
      const row = el('div', { class: 'row text-row' }, el('span', { class: 'label' }, control.label), el('div', { class: 'tz-wrap' }, field, mountTzClock(control.value)))
      if (control.hint !== undefined)
        row.append(el('div', { class: 'hint' }, control.hint)) // parity with the textarea path — the field may carry a hint
      return row
    }
    // A tappable row (label + one-line preview + chevron — the same .row-tap skin as every row that opens
    // something) instead of an inline textarea: tapping it opens the full-screen editor sheet
    // (text-editor.ts), where the text is readable and comfortable to write (user call, session 70 — the
    // old inline boxes were too cramped). The commit model is unchanged — the sheet auto-saves on
    // blur/close like the textarea did — and the hint ({name} for welcomeText) moved into the sheet.
    const preview = el('span', { class: 'value value-preview' }, textRowPreview(control.value, control.list))
    const row = el('button', { class: 'row row-tap text-tap', type: 'button' }, el('span', { class: 'label' }, control.label), preview, chevronIcon('row-chevron'))
    row.addEventListener('click', () => openTextEditor(control, sectionKey, onChange, row))
    return row
  }

  // A <label> wraps the control so tapping the text activates it (a11y, no id wiring needed).
  const row = el('label', { class: 'row' }, el('span', { class: 'label' }, control.label))

  if (control.kind === 'bool') {
    const input = el('input', { type: 'checkbox', checked: control.value })
    input.addEventListener('change', () => onChange(control.patch((input as HTMLInputElement).checked)))
    row.append(input)
  }
  else if (control.kind === 'enum') {
    const select = el('select')
    for (const choice of control.choices)
      select.append(el('option', { value: choice.value, selected: choice.value === control.value }, choice.label))
    select.addEventListener('change', () => onChange(control.patch((select as HTMLSelectElement).value)))
    row.append(select)
  }
  else {
    const select = el('select')
    for (const choice of control.choices)
      select.append(el('option', { value: String(choice.value), selected: choice.value === control.value }, choice.label))
    select.addEventListener('change', () => onChange(control.patch(Number((select as HTMLSelectElement).value))))
    row.append(select)
  }
  return row
}

/** Build the form DOM from the current settings, wiring every control's change to onChange(patch). */
export function renderForm(root: HTMLElement, settings: GroupSettings, onChange: (patch: GroupSettingsPatch) => void, sections: readonly SectionSpec[] = SECTIONS): void {
  clear(root)
  for (const section of planForm(settings, sections).sections) {
    // Section header: an outline icon + the (prominent) title + a guide button that opens the per-section
    // help sheet. The icon is decorative (aria-hidden in icons.ts); the title carries the meaning.
    const head = el('div', { class: 'sec-head' }, sectionIcon(section.key, 'sec-icon'), el('h2', { class: 'sec-title' }, section.title))
    // The guide trigger is a plain bold "?" text (R7 — no SVG circle); the <button>'s aria-label carries the
    // accessible name, so the "?" is purely visual.
    const help = el('button', { 'class': 'help-btn', 'type': 'button', 'aria-label': `${t('ui-about')} ${section.title}` }, '?')
    help.addEventListener('click', () => openHelp(section.key, help))
    head.append(help)
    root.append(head)
    // Grey out + disable any INERT control (controlInactive): a section whose master "Enabled" is off
    // greys its other rows (R4), AND a field gated by `activeWhen` greys when its dependency isn't met
    // (e.g. captcha timeout/failAction while `type` is 'none'). One rule for both; the master stays live.
    // Each section's rows live in one tray (.sec-group). The two data-* marks are for the CSS only: the
    // master switch, and a "set" — a section made only of independent on/off rows with no master (locks,
    // cleanup), which the «Pannello» look lays out as pills.
    const isSet = section.controls.every(c => c.kind === 'bool' && !c.master)
    const group = el('div', { class: 'sec-group', ...(isSet ? { 'data-shape': 'set' } : {}) })
    for (const control of section.controls) {
      const rowEl = renderControl(control, section.key, onChange)
      if (control.kind === 'bool' && control.master)
        rowEl.dataset.master = ''
      if (controlInactive(settings, section.key, control.field))
        rowEl.classList.add('off')
      group.append(rowEl)
    }
    root.append(group)
  }
}
